// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * Table component for displaying organization-wide audit events with
 * server-side pagination. Supports filtering by date range and object type.
 * Events are sorted by date in descending order by the server.
 */
import { AuditEvent, AuditSourceType } from '@/audit/api/types';
import { AuditEventLabel } from '@/audit/components/audit-event-label';
import { AuditEventObjectLink } from '@/audit/components/audit-event-object-link';
import { AuditPropertyLabel } from '@/audit/components/audit-property-label';
import { FullAuditTableFilterRow } from '@/audit/components/full-audit-table-filter-row';
import { MultiTimezoneTooltip } from '@/components/multi-timezone-tooltip';
import { TablePaginationFooter } from '@/components/table-pagination-footer';
import {
  Box,
  LinearProgress,
  Paper,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import { DateTime } from 'luxon';

// Minimum width for horizontal scrolling on narrow screens
const MIN_TABLE_WIDTH = 1100;

// Grid template for consistent column widths between header and rows
const GRID_TEMPLATE_COLUMNS =
  'minmax(100px, 0.8fr) minmax(80px, 0.6fr) minmax(120px, 1fr) minmax(200px, 2fr) minmax(80px, 0.6fr) minmax(100px, 0.8fr)';

// Source type labels for display
const sourceTypeLabels: Record<AuditSourceType, string> = {
  UPLOAD: 'Upload',
  API_KEY: 'API key',
  CARRIER_UPDATE: 'Shipping carrier update',
  HUBSPOT_API: 'HubSpot API',
  SFDC_API: 'Salesforce API',
  SFDC_SYNC: 'Salesforce sync',
  THIRD_PARTY_API: 'Third party API',
  NETSUITE: 'NetSuite API',
  QBO: 'QuickBooks API',
  OAUTH: 'Connected application',
  INHERIT: 'Inherit',
};

// Static source type filter options including "Web" (which is the default when no source is set)
const SOURCE_TYPE_OPTIONS: { label: string; value: string }[] = [
  { label: 'Web', value: 'WEB' },
  { label: 'Upload', value: 'UPLOAD' },
  { label: 'API key', value: 'API_KEY' },
  { label: 'Shipping carrier update', value: 'CARRIER_UPDATE' },
  { label: 'HubSpot API', value: 'HUBSPOT_API' },
  { label: 'Salesforce API', value: 'SFDC_API' },
  { label: 'Salesforce sync', value: 'SFDC_SYNC' },
  { label: 'Third party API', value: 'THIRD_PARTY_API' },
  { label: 'NetSuite API', value: 'NETSUITE' },
  { label: 'QuickBooks API', value: 'QBO' },
  { label: 'Connected application', value: 'OAUTH' },
];

// Mapping from backend reference_relation values to display labels
const REFERENCE_RELATION_OPTIONS: { label: string; value: string }[] = [
  { label: 'Asset', value: 'ASSET' },
  { label: 'Cost', value: 'COST' },
  { label: 'Customer', value: 'CUSTOMER' },
  { label: 'Location', value: 'LOCATION' },
  { label: 'Note', value: 'NOTE' },
  { label: 'Organization', value: 'ORGANIZATION' },
  { label: 'User', value: 'PERSON' },
  { label: 'Product', value: 'PRODUCT' },
  { label: 'Project', value: 'PROJECT' },
  { label: 'Shipment', value: 'SHIPMENT' },
  { label: 'Ticket', value: 'TICKET' },
  { label: 'Time record', value: 'TIME_ACTIVITY' },
];

// Number of skeleton rows to show during initial loading
const SKELETON_ROW_COUNT = 8;

interface Props {
  events: AuditEvent[];
  totalCount: number | null;
  page: number;
  pageSize: number;
  isLoading: boolean;
  isFetching: boolean;
  startDate: DateTime;
  endDate: DateTime;
  referenceRelations: string[];
  sourceTypes: string[];
  actorOptions: { id: string; label: string }[];
  actorIds: string[];
  onStartDateChange: (date: DateTime | null) => void;
  onEndDateChange: (date: DateTime | null) => void;
  onReferenceRelationChange: (relations: string[]) => void;
  onSourceTypeChange: (sourceTypes: string[]) => void;
  onActorChange: (actorIds: string[]) => void;
  onPageChange: (page: number) => void;
}

interface AuditRowProps {
  event: AuditEvent;
}

/**
 * A single row in the audit table.
 */
function AuditRow(props: AuditRowProps) {
  const { event } = props;

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: GRID_TEMPLATE_COLUMNS,
        alignItems: 'flex-start',
        borderBottom: '1px solid',
        borderColor: 'divider',
        px: 2,
        py: 1,
      }}
    >
      <Box sx={{ minWidth: 0, overflow: 'hidden' }}>
        <MultiTimezoneTooltip timestamp={event.triggeredAt}>
          <Stack>
            <Typography variant="body2" color="text.secondary">
              {event.triggeredAt.toLocal().toFormat('MMM dd, yyyy')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {event.triggeredAt.toLocal().toLocaleString({
                timeStyle: 'long',
              })}
            </Typography>
          </Stack>
        </MultiTimezoneTooltip>
      </Box>
      <Box sx={{ minWidth: 0, overflow: 'hidden' }}>
        <Typography variant="body2" noWrap>
          {event.context.eventTargetObject.typeName}
        </Typography>
      </Box>
      <Box sx={{ minWidth: 0, overflow: 'hidden' }}>
        <AuditEventObjectLink context={event.context} />
      </Box>
      <Box sx={{ minWidth: 0, overflow: 'hidden' }}>
        <AuditEventLabel
          context={event.context}
          eventIdentifier={event.eventIdentifier}
        />
        <Stack sx={{ pl: 2 }}>
          {event.context.properties.map((property) => (
            <AuditPropertyLabel
              key={property.id}
              property={property}
              isDelete={event.context.action === 'DELETE'}
              action={event.context.action}
              context={event.context}
            />
          ))}
        </Stack>
      </Box>
      <Box sx={{ minWidth: 0, overflow: 'hidden' }}>
        <Typography variant="body2" noWrap>
          {event.context.source
            ? sourceTypeLabels[event.context.source.type]
            : 'Web'}
        </Typography>
      </Box>
      <Box sx={{ minWidth: 0, overflow: 'hidden' }}>
        <Typography variant="body2" noWrap>
          {event.context.actor.name}
        </Typography>
      </Box>
    </Box>
  );
}

function HeaderRow() {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: GRID_TEMPLATE_COLUMNS,
        alignItems: 'center',
        borderBottom: '1px solid',
        borderColor: 'divider',
        bgcolor: 'grey.100',
        px: 2,
        py: 1,
      }}
    >
      <Box>
        <Typography variant="overline">Date</Typography>
      </Box>
      <Box>
        <Typography variant="overline">Type</Typography>
      </Box>
      <Box>
        <Typography variant="overline">Record</Typography>
      </Box>
      <Box>
        <Typography variant="overline">Event</Typography>
      </Box>
      <Box>
        <Typography variant="overline">Source</Typography>
      </Box>
      <Box>
        <Typography variant="overline">Actor</Typography>
      </Box>
    </Box>
  );
}

function SkeletonRow() {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: GRID_TEMPLATE_COLUMNS,
        alignItems: 'flex-start',
        borderBottom: '1px solid',
        borderColor: 'divider',
        px: 2,
        py: 1,
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Skeleton width="80%" height={20} />
        <Skeleton width="60%" height={20} />
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Skeleton width="70%" height={20} />
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Skeleton width="85%" height={20} />
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Skeleton width="90%" height={20} />
        <Skeleton width="60%" height={16} sx={{ ml: 2 }} />
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Skeleton width="65%" height={20} />
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Skeleton width="75%" height={20} />
      </Box>
    </Box>
  );
}

/**
 * A table component that displays organization-wide audit events with
 * server-side pagination and filtering capabilities.
 */
export function FullAuditTable(props: Props) {
  const {
    events,
    totalCount,
    page,
    pageSize,
    isLoading,
    isFetching,
    startDate,
    endDate,
    referenceRelations,
    sourceTypes,
    actorOptions,
    actorIds,
    onStartDateChange,
    onEndDateChange,
    onReferenceRelationChange,
    onSourceTypeChange,
    onActorChange,
    onPageChange,
  } = props;

  const showSkeleton = isLoading && events.length === 0;

  const typeOptions = REFERENCE_RELATION_OPTIONS.map((option) => ({
    id: option.value,
    label: option.label,
    isActive: referenceRelations.includes(option.value),
  }));

  const sourceOptions = SOURCE_TYPE_OPTIONS.map((option) => ({
    id: option.value,
    label: option.label,
    isActive: sourceTypes.includes(option.value),
  }));

  const actorFilterOptions = actorOptions.map((option) => ({
    id: option.id,
    label: option.label,
    isActive: actorIds.includes(option.id),
  }));

  const handleTypeFilterChange = (value: string, active: boolean) => {
    const updated = active
      ? [...referenceRelations, value]
      : referenceRelations.filter((r) => r !== value);
    onReferenceRelationChange(updated);
  };

  const handleSourceFilterChange = (value: string, active: boolean) => {
    const updated = active
      ? [...sourceTypes, value]
      : sourceTypes.filter((s) => s !== value);
    onSourceTypeChange(updated);
  };

  const handleActorFilterChange = (value: string, active: boolean) => {
    const updated = active
      ? [...actorIds, value]
      : actorIds.filter((a) => a !== value);
    onActorChange(updated);
  };

  return (
    <Stack spacing={0}>
      <FullAuditTableFilterRow
        startDate={startDate}
        endDate={endDate}
        typeOptions={typeOptions}
        sourceOptions={sourceOptions}
        actorOptions={actorFilterOptions}
        onStartDateChange={onStartDateChange}
        onEndDateChange={onEndDateChange}
        onTypeFilterChange={handleTypeFilterChange}
        onSourceFilterChange={handleSourceFilterChange}
        onActorFilterChange={handleActorFilterChange}
      />

      <Paper variant="outlined" sx={{ overflowX: 'auto' }}>
        {isFetching && !showSkeleton && <LinearProgress />}
        <Box sx={{ minWidth: MIN_TABLE_WIDTH }}>
          <HeaderRow />

          {showSkeleton ? (
            Array.from({ length: SKELETON_ROW_COUNT }, (_, index) => (
              <SkeletonRow key={index} />
            ))
          ) : events.length === 0 ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                No audit events found
              </Typography>
            </Box>
          ) : (
            events.map((event) => <AuditRow key={event.id} event={event} />)
          )}
        </Box>
      </Paper>

      <TablePaginationFooter
        pagination={{
          page,
          pageSize,
          onPageChange,
        }}
        totalCount={totalCount}
        entityOptions={{ singular: 'audit event' }}
      />
    </Stack>
  );
}
